/**
 * HMCGuestPage Component
 *
 * Public-facing page for guests to view events via a shareable link.
 * No authentication required - access is validated via the share token.
 *
 * Features:
 * - Validates share token on mount
 * - Shows loading/error states
 * - Renders events in forced guest mode (ticket limits, no admin controls)
 * - Full ticket request functionality
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GuestViewProvider, useGuestView } from './GuestViewContext';
import { GuestLinkError } from './components/GuestLinkError';
import { GuestMobileView } from './components/GuestMobileView';
import { GuestDesktopSplitView } from './components/GuestDesktopSplitView';
import {
  EventTypeFilter,
  defaultEventTypeFilters,
  filterEventsByType,
  type EventTypeFilters,
} from './components/EventTypeFilter';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import type { HMCEvent, UploadHistory } from './hmcEventsStore';

// Guest API response type
interface GuestAccessResponse {
  events: HMCEvent[];
  report_name: string;
  event_count: number;
  expires_at: string;
  label: string | null;
}

// Demo mode mock data for testing
// Team logo URLs from Wikipedia Commons (reliable CDN)
const DEMO_EVENTS: HMCEvent[] = [
  {
    id: 1,
    event_type: 'dynamo',
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    kickoff_time: '7:30 PM',
    opponent: 'LA Galaxy',
    venue: 'Shell Energy Stadium',
    broadcast: 'Apple TV',
    special_event: '10 available',
    is_home: true,
    event_url: 'https://www.houstondynamo.com',
    event_description: 'Join us for an exciting MLS match as the Dynamo take on LA Galaxy in this Western Conference showdown!',
    event_start_time: '7:30 PM',
    music_genre: null,
    is_family_friendly: true,
    // Houston Dynamo FC logo from Wikipedia Commons
    event_image_url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/Houston_Dynamo_FC_logo.svg/250px-Houston_Dynamo_FC_logo.svg.png',
    doors_open: '6:00 PM',
    enrichment_status: 'completed',
    youtube_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    upload_id: 'demo',
  },
  {
    id: 2,
    event_type: 'dash',
    event_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    kickoff_time: '7:00 PM',
    opponent: 'Kansas City Current',
    venue: 'Shell Energy Stadium',
    broadcast: 'CBS Sports',
    special_event: '8 available',
    is_home: true,
    event_url: 'https://www.houstondash.com',
    event_description: 'Watch the Houston Dash battle it out in this NWSL match against Kansas City Current!',
    event_start_time: '7:00 PM',
    music_genre: null,
    is_family_friendly: true,
    // Houston Dash logo from Wikipedia Commons
    event_image_url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4b/Houston_Dash_%282020%29_logo.svg/250px-Houston_Dash_%282020%29_logo.svg.png',
    doors_open: '5:30 PM',
    enrichment_status: 'completed',
    youtube_url: null,
    upload_id: 'demo',
  },
  {
    id: 3,
    event_type: 'general',
    event_date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    kickoff_time: '8:00 PM',
    opponent: 'Drake',
    venue: 'Toyota Center',
    broadcast: null,
    special_event: '4 available',
    is_home: true,
    event_url: 'https://www.toyotacenter.com',
    event_description: 'Experience Drake live in concert at Toyota Center. A night of chart-topping hits you won\'t forget!',
    event_start_time: '8:00 PM',
    music_genre: 'rap',
    is_family_friendly: false,
    event_image_url: null,
    doors_open: '6:30 PM',
    enrichment_status: 'completed',
    youtube_url: 'https://www.youtube.com/watch?v=xpVfcZ0ZcFM',
    upload_id: 'demo',
  },
  {
    id: 4,
    event_type: 'general',
    event_date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    kickoff_time: '7:00 PM',
    opponent: 'Houston Rockets vs Golden State Warriors',
    venue: 'Toyota Center',
    broadcast: 'ESPN',
    special_event: '6 available',
    is_home: true,
    event_url: 'https://www.rockets.com',
    event_description: 'NBA action as the Houston Rockets face off against the Golden State Warriors in this must-see matchup!',
    event_start_time: '7:00 PM',
    music_genre: null,
    is_family_friendly: true,
    // Houston Rockets logo from Wikipedia Commons
    event_image_url: 'https://upload.wikimedia.org/wikipedia/en/thumb/2/28/Houston_Rockets.svg/250px-Houston_Rockets.svg.png',
    doors_open: '5:30 PM',
    enrichment_status: 'completed',
    youtube_url: null,
    upload_id: 'demo',
  },
] as unknown as HMCEvent[];

// Loading state component
const GuestPageLoading: React.FC = () => (
  <div className="min-h-screen bg-dark-bg flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
      <p className="text-dark-muted">Loading events...</p>
    </div>
  </div>
);

/**
 * Check if we're on the guest portal subdomain
 */
const isGuestSubdomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.toLowerCase() === 'usibenefits-events.eroland.me';
};

/**
 * Extract share ID from URL
 * - On guest subdomain: extract from pathname (e.g., /abc123 -> abc123)
 * - On main domain: use React Router's useParams
 */
const getShareIdFromUrl = (): string | undefined => {
  if (isGuestSubdomain()) {
    // On subdomain, the share token is the entire path after the leading slash
    const pathname = window.location.pathname;
    const shareId = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    return shareId || undefined;
  }
  return undefined;
};

export const HMCGuestPage: React.FC = () => {
  const params = useParams<{ shareId: string }>();
  // Support both React Router params and direct URL extraction for subdomain
  const shareId = params.shareId || getShareIdFromUrl();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{
    type: 'expired' | 'revoked' | 'invalid' | 'not_found' | 'error';
    message?: string;
  } | null>(null);
  const [guestData, setGuestData] = useState<GuestAccessResponse | null>(null);

  // Fetch guest events on mount
  const fetchGuestEvents = useCallback(async () => {
    if (!shareId) {
      setError({ type: 'invalid', message: 'No share link provided.' });
      setLoading(false);
      return;
    }

    // Demo mode for testing - use mock data
    if (shareId === 'demo' || shareId === 'preview') {
      setGuestData({
        events: DEMO_EVENTS,
        report_name: 'Demo Events',
        event_count: DEMO_EVENTS.length,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        label: 'Demo Preview',
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/hmc/guest/${encodeURIComponent(shareId)}`);

      if (!response.ok) {
        // Handle specific error codes
        if (response.status === 410) {
          // Gone - expired or revoked
          const data = await response.json().catch(() => ({}));
          const message = data.detail || '';

          if (message.toLowerCase().includes('expired')) {
            setError({ type: 'expired', message });
          } else if (message.toLowerCase().includes('revoked')) {
            setError({ type: 'revoked', message });
          } else {
            setError({ type: 'expired', message });
          }
        } else if (response.status === 404) {
          setError({ type: 'not_found' });
        } else {
          setError({ type: 'error', message: 'Failed to load events. Please try again.' });
        }
        return;
      }

      const data: GuestAccessResponse = await response.json();
      setGuestData(data);
    } catch (err) {
      console.error('[HMCGuestPage] Failed to fetch guest events:', err);
      setError({ type: 'error', message: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    fetchGuestEvents();
  }, [fetchGuestEvents]);

  // Set custom page title and favicon for guest view
  useEffect(() => {
    // Save original title and favicon to restore later (if needed)
    const originalTitle = document.title;
    const originalFavicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')?.href;

    // Set custom title
    document.title = 'USI Suite Availability';

    // Create red ticket favicon as SVG data URL
    const ticketSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ef4444" stroke="%23ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>`;
    const faviconDataUrl = `data:image/svg+xml,${ticketSvg}`;

    // Update favicon
    let faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (faviconLink) {
      faviconLink.href = faviconDataUrl;
    } else {
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = faviconDataUrl;
      document.head.appendChild(faviconLink);
    }

    // Cleanup: restore original title and favicon when leaving page
    return () => {
      document.title = originalTitle;
      if (originalFavicon && faviconLink) {
        faviconLink.href = originalFavicon;
      }
    };
  }, []);

  // Loading state
  if (loading) {
    return <GuestPageLoading />;
  }

  // Error state
  if (error) {
    return <GuestLinkError errorType={error.type} message={error.message} />;
  }

  // No data (shouldn't happen but handle gracefully)
  if (!guestData) {
    return <GuestLinkError errorType="error" message="Unable to load event data." />;
  }

  // Create a mock UploadHistory for EventReportViewer compatibility
  const mockReportInfo: UploadHistory = {
    id: 0,
    upload_id: shareId || '',
    filename: guestData.report_name,
    file_size: 0,
    events_created: guestData.event_count,
    events_updated: 0,
    enrichment_status: 'completed',
    error_message: null,
    created_at: new Date().toISOString(),
    processed_at: new Date().toISOString(),
  };

  return (
    <GuestViewProvider initialGuestMode={true}>
      <GuestPageContent
        events={guestData.events}
        reportInfo={mockReportInfo}
        reportName={guestData.label || guestData.report_name}
        eventCount={guestData.event_count}
        expiresAt={guestData.expires_at}
      />
    </GuestViewProvider>
  );
};

// Inner component to access GuestViewContext
const GuestPageContent: React.FC<{
  events: HMCEvent[];
  reportInfo: UploadHistory;
  reportName: string;
  eventCount: number;
  expiresAt: string;
}> = ({ events }) => {
  const { maxGuestTickets } = useGuestView();
  const isMobile = useIsMobile();

  // Event type filters - all selected by default (for mobile standalone)
  const [filters, setFilters] = useState<EventTypeFilters>(defaultEventTypeFilters);

  // Filter events based on active filters
  const filteredEvents = useMemo(
    () => filterEventsByType(events, filters),
    [events, filters]
  );

  // Desktop: Split view with table on right (handles its own filters)
  // Mobile: Full-screen mobile view with filters
  if (isMobile) {
    return (
      <div className="public-scrollable-page">
        <GuestMobileView
          events={filteredEvents}
          maxGuestTickets={maxGuestTickets}
          filterHeader={
            <EventTypeFilter
              filters={filters}
              onChange={setFilters}
              compact={true}
            />
          }
        />
      </div>
    );
  }

  // Desktop split view (handles its own filter state)
  return (
    <GuestDesktopSplitView events={events} maxGuestTickets={maxGuestTickets} />
  );
};

export default HMCGuestPage;
